import React from 'react';

// Lightweight inline-SVG line chart of weight over time. We keep this
// dependency-free so dashboards stay snappy and we don't pay a charts library
// just to render one panel. Caller passes `entries` sorted ascending by
// recordedAt (or createdAt) with shape: { weightKg, recordedAt }.
function WeightChart({ entries = [], height = 180 }) {
  const data = entries
    .filter((e) => typeof e.weightKg === 'number' && e.weightKg > 0)
    .map((e) => ({ t: new Date(e.recordedAt || e.createdAt).getTime(), w: e.weightKg }))
    .sort((a, b) => a.t - b.t);

  if (data.length < 2) {
    return (
      <div className="muted text-sm" style={{ padding: 16, textAlign: 'center' }}>
        Not enough weigh-ins yet to chart progress. Add at least two entries to see a trend.
      </div>
    );
  }

  const padding = { top: 16, right: 16, bottom: 28, left: 36 };
  const width = 560;
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const minT = data[0].t;
  const maxT = data[data.length - 1].t;
  const tSpan = Math.max(1, maxT - minT);
  const minW = Math.min(...data.map((d) => d.w));
  const maxW = Math.max(...data.map((d) => d.w));
  const wSpan = Math.max(0.5, maxW - minW);

  function x(t) { return padding.left + ((t - minT) / tSpan) * innerW; }
  function y(w) { return padding.top + (1 - (w - minW) / wSpan) * innerH; }

  const points = data.map((d) => `${x(d.t)},${y(d.w)}`).join(' ');
  const first = data[0];
  const last = data[data.length - 1];
  const delta = last.w - first.w;

  return (
    <div>
      <div className="row" style={{ gap: 12, alignItems: 'baseline', marginBottom: 6 }}>
        <span className="muted text-xs">Weight trend</span>
        <span className="bold">{first.w.toFixed(1)} → {last.w.toFixed(1)} kg</span>
        <span className={delta < 0 ? 'text-success' : 'text-danger'}>
          {delta > 0 ? '+' : ''}{delta.toFixed(1)} kg
        </span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height }}>
        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={height - padding.bottom} stroke="var(--ink-300)" />
        <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} stroke="var(--ink-300)" />

        {[minW, (minW + maxW) / 2, maxW].map((w, i) => (
          <g key={i}>
            <text x={padding.left - 6} y={y(w) + 4} textAnchor="end" fontSize="10" fill="var(--ink-500)">
              {w.toFixed(1)}
            </text>
            <line x1={padding.left} y1={y(w)} x2={width - padding.right} y2={y(w)} stroke="var(--ink-200)" strokeDasharray="3 3" />
          </g>
        ))}

        <polyline fill="none" stroke="var(--brand-700)" strokeWidth="2" points={points} />
        {data.map((d, i) => (
          <circle key={i} cx={x(d.t)} cy={y(d.w)} r={3} fill="var(--brand-700)" />
        ))}

        <text x={padding.left} y={height - 8} fontSize="10" fill="var(--ink-500)">
          {new Date(minT).toLocaleDateString()}
        </text>
        <text x={width - padding.right} y={height - 8} fontSize="10" fill="var(--ink-500)" textAnchor="end">
          {new Date(maxT).toLocaleDateString()}
        </text>
      </svg>
    </div>
  );
}

export default WeightChart;
